import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, MoreVertical, Pencil, Power, Trash2 } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useDeleteClient, useGetClient, useGetClientCars } from "../../hooks/use-clients";
import { useLocation, useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { ClientSkeleton } from "../users-skeleton";
import { formatDistance } from "date-fns";
import { toast } from "sonner";
import { useState } from "react";

export const ClientDetail = ({ clientId }: { clientId: string }) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const navigate = useNavigate();
  const { client, error } = useGetClient(clientId);
  const { cars, loading } = useGetClientCars(clientId);
  const { deleteClient } = useDeleteClient(clientId);
  const { state } = useLocation();

  const backToUserPage = () => {
    navigate("/users", { state: { role: "Client", page: state?.page ?? 1 } });
  };
  const handleDeleteClient = async () => {
    try {
      if (clientId) {
        await deleteClient();
        toast.success("Client deleted successfully");
        backToUserPage();
      }
    } catch {
      toast.error(error?.message ?? "Failed to delete user");
    }
  };

  const formatLicensePlate = (plate: string) => {
    return `${plate.substring(0, 2)}-${plate.substring(2, 5)}-${plate.substring(5, 7)}`;
  };

  return (
    <div className="container mx-auto py-8">
      <div className="flex justify-between items-center mb-6">
        <Button variant="outline" onClick={backToUserPage} className="flex items-center gap-2">
          <ArrowLeft className="h-4 w-4" />
          Go Back
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem className="flex items-center" disabled>
              <Pencil className="mr-2 h-4 w-4" />
              <span>Edit</span>
            </DropdownMenuItem>
            <DropdownMenuItem className="flex items-center" disabled>
              <Power className="mr-2 h-4 w-4" />
              <span>Disable client</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="flex items-center text-destructive focus:text-destructive"
              onClick={() => setShowDeleteDialog(true)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              <span>Delete</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {loading ? (
        <ClientSkeleton />
      ) : (
        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-1">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">Client Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex justify-center mb-6">
                  <Avatar className="h-20 w-20">
                    <AvatarFallback className="text-2xl">
                      {client?.firstName[0]}
                      {client?.lastName[0]}
                    </AvatarFallback>
                  </Avatar>
                </div>

                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium">First name</TableCell>
                      <TableCell>{client?.firstName}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Last name</TableCell>
                      <TableCell>{client?.lastName}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Phone Number</TableCell>
                      <TableCell>
                        <a href={`tel:${client?.phoneNumber}`} className="text-primary hover:underline">
                          {client?.phoneNumber}
                        </a>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Last login</TableCell>
                      <TableCell>
                        {client?.lastLoginDate
                          ? formatDistance(new Date(client?.lastLoginDate), new Date(), { addSuffix: true })
                          : ""}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>

          <div className="col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">Registered Vehicles</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Plate</TableHead>
                      <TableHead>Brand</TableHead>
                      <TableHead>Model</TableHead>
                      <TableHead className="text-center">Default</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cars.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">No cars registered</div>
                    ) : (
                      cars.map((car, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{formatLicensePlate(car.license_plate)}</TableCell>
                          <TableCell>{car.brand}</TableCell>
                          <TableCell>{car.model}</TableCell>
                          <TableCell className="text-center">{car.is_default ? "✔️" : "❌"}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Client</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this client? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteClient}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
